import { AxiosResponse } from "axios";
import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router";

import * as Sentry from "@sentry/browser";
import { Button, Col, Row, Spin, message } from "antd";
import { FormInstance } from "antd/lib/form/Form";
import AttachmentsSection from "../components/AttachmentsSection";
import Card from "../components/Card";
import BaseForm from "../components/Form/BaseForm";
import {
  ASSIGN_ATTACHMENT_TO_TEMPLATE,
  ASSIGN_CC_CONTACT_LEVEL_TO_TEMPLATE,
  ASSIGN_CONTACT_LEVEL_TO_TEMPLATE,
  CREATE_EMAIL_TEMPLATE,
  GET_CONTACT_LEVELS,
  GET_TEMPLATE_CATEGORIES,
} from "../gql/templates";
import {
  APIError,
  Attachment,
  ContactLevel,
  Template,
  TemplateCategory,
  TemplateRecipientContactLevel,
} from "../helpers/shared-interfaces";
import { buildTemplateFormFields } from "../helpers/template";
import {
  generateDisplayErrorMessage,
  handleValidationErrors,
  request,
  responseHandler,
} from "../helpers/utilities";
import { CREATE_ATTACHMENT } from "../gql/general";

interface State {
  isLoading: boolean;
  contactLevels: ContactLevel[];
  templateCategories: TemplateCategory[];
  attachmentIds: string[];
  attachments: Attachment[];
}

export default class NewEmailTemplate extends React.Component<
  RouteComponentProps,
  State
> {
  reference: RefObject<FormInstance>;
  attachmentsReference: RefObject<FormInstance>;

  constructor(props: Readonly<RouteComponentProps>) {
    super(props);
    this.state = {
      isLoading: true,
      contactLevels: [],
      templateCategories: [],
      attachmentIds: [],
      attachments: [],
    };

    this.reference = React.createRef<FormInstance>();
    this.attachmentsReference = React.createRef<FormInstance>();
  }
  _isMounted = false;

  setRef = (
    name: "reference" | "attachmentsReference",
    ref: React.RefObject<FormInstance>,
  ): void => {
    this[name] = ref;
  };

  redirectToEmailsPage(): void {
    this.props.history.push(`/communication-templates`);
  }

  getContactLevels = async (): Promise<void> => {
    this.setState({ isLoading: true });
    try {
      const [
        {
          data: {
            data: { contactLevels },
          },
        },
        {
          data: {
            data: { templateCategories },
          },
        },
      ]: [
        AxiosResponse<{
          data: {
            contactLevels: ContactLevel[];
          };
        }>,
        AxiosResponse<{
          data: {
            templateCategories: {
              results: TemplateCategory[];
            };
          };
        }>,
      ] = await Promise.all([
        request({
          query: GET_CONTACT_LEVELS,
        }),
        request({
          query: GET_TEMPLATE_CATEGORIES,
          variables: { numberPerPage: 100 },
        }),
      ]);

      this.setState({
        contactLevels,
        templateCategories: templateCategories.results,
        isLoading: false,
      });
    } catch (error) {
      this.setState({ isLoading: false });
    }
  };

  assignContactLevelToTemplate = async (props: {
    contactLevelIds: string[];
    templateId: string;
    isCC?: boolean;
  }): Promise<void> => {
    interface Response {
      data: {
        assignContactLevelToTemplate: {
          customerInteractionNote: {
            recipientTemplateContactLevel: TemplateRecipientContactLevel;
          };
        };
      };
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: props.isCC
          ? ASSIGN_CC_CONTACT_LEVEL_TO_TEMPLATE
          : ASSIGN_CONTACT_LEVEL_TO_TEMPLATE,
        variables: {
          contactLevelIds: props.contactLevelIds,
          templateId: props.templateId,
        },
      });

      const { err } = responseHandler<Response>(
        response,
        "assignContactLevelToTemplate",
      );

      if (err) {
        message.error(`failed to assign contact level to template - ${generateDisplayErrorMessage(err)}`);
      }
    } catch (error) {
      Sentry.captureException(error);
    }

    this.setState({ isLoading: false });
  };

  componentDidMount(): void {
    this._isMounted = true;
    this.getContactLevels();
  }

  componentWillUnmount(): void {
    this._isMounted = false;
  }

  createEmailTemplate = async (): Promise<void> => {
    interface Response {
      data?: {
        createTemplate?: {
          template?: Template;
          errors?: APIError[];
        };
      };
      errors?: Record<string, unknown>[];
    }

    if (!this.reference.current) {
      return;
    }

    let data;

    try {
      data = await this.reference.current.validateFields([
        "body",
        "name",
        "emailSubject",
        "templateType",
        "templateCategoryId",
        "contactLevelIds",
        "ccContactLevelIds",
      ]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    try {
      this.setState({
        isLoading: true,
      });
      const response: AxiosResponse<Response> = await request({
        query: CREATE_EMAIL_TEMPLATE,
        variables: {
          emailSubject: data.emailSubject,
          templateType: data.templateType,
          templateCategoryId: data.templateCategoryId,
          name: data.name,
          body: data.body,
        },
      });

      if (response.data && response.data.data?.createTemplate) {
        const template = response.data.data?.createTemplate
          .template as Template;

        if (
          response.data.data?.createTemplate?.errors &&
          response.data.data?.createTemplate?.errors?.length > 0
        ) {
          message.error(
            generateDisplayErrorMessage(
              response.data.data?.createTemplate.errors,
            ),
          );
          this.setState({
            isLoading: false,
          });
          return;
        }

        if (this.state.attachmentIds.length > 0) {
          this.assignAttachmentToTemplate(
            this.state.attachmentIds,
            template.id,
          );
        }

        if (data.contactLevelIds) {
          await this.assignContactLevelToTemplate({
            contactLevelIds: data.contactLevelIds,
            templateId: template.id,
          });
        }
        if (data.ccContactLevelIds) {
          await this.assignContactLevelToTemplate({
            contactLevelIds: data.ccContactLevelIds,
            templateId: template.id,
            isCC: true,
          });
        }
        message.success("Email Template created sucessfully");
        this.redirectToEmailsPage();
      } else {
        message.error("Failed to create Email Template");
      }
    } catch (error) {
      message.error(`Failed to create Template - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
    this.setState({
      isLoading: false,
    });
  };

  createAttachment = async (combinedInfo: string): Promise<void> => {
    interface Response {
      attachment: Attachment;
    }

    const fieldsToBeValidated: string[] = ["url"];

    this.setState({ isLoading: true });

    try {
      const [url, name] = combinedInfo.split("@@@");

      const response: AxiosResponse = await request({
        query: CREATE_ATTACHMENT,
        variables: {
          url,
          name,
        },
      });
      this.setState({ isLoading: false });

      const { mutation } = responseHandler<Response>(
        response,
        "createAttachment",
      );
      this.setState((state) => ({
        attachmentIds: [...state.attachmentIds, mutation.attachment.id],
        attachments: [...state.attachments, mutation.attachment],
      }));
      this.attachmentsReference.current &&
        this.attachmentsReference.current.resetFields(fieldsToBeValidated);
    } catch (error) {
      Sentry.captureException(error);
      this.setState({ isLoading: false });
    }
  };

  removeAttachment = (attachmentId: string): void => {
    this.setState((state) => ({
      attachments: state.attachments.filter(
        (attachment) => attachment.id !== attachmentId,
      ),
      attachmentIds: state.attachmentIds.filter((id) => id !== attachmentId),
    }));
  };

  assignAttachmentToTemplate = async (
    attachmentIds: string[],
    templateId: string,
  ): Promise<void> => {
    this.setState({ isLoading: true });

    interface Response {
      data: {
        assignAttachmentsToTemplate: {
          template: {
            templateAttachments: {
              attachment: Attachment;
            };
          };
        };
      };
    }

    return request({
      query: ASSIGN_ATTACHMENT_TO_TEMPLATE,
      variables: {
        attachmentIds,
        templateId,
      },
    }).then(({ data }: AxiosResponse<Response>): void => {
      this.setState((oldState) => ({
        isLoading: false,
        attachments: [
          ...oldState.attachments,
          data.data.assignAttachmentsToTemplate.template.templateAttachments
            ?.attachment,
        ],
      }));
    });
  };

  render(): JSX.Element | null {
    const { isLoading, contactLevels, templateCategories } = this.state;
    const fields = buildTemplateFormFields({
      contactLevels,
      templateCategories,
    });

    return (
      <Card header="">
        <div className="create-department-container" style={{}}>
          <Spin spinning={isLoading}>
            <Row>
              <Col flex="auto">
                <BaseForm
                  size="large"
                  reference={this.reference}
                  layout="vertical"
                  fields={[...fields]}
                  fieldsToHide={[]}
                />
                <AttachmentsSection
                  reference={this.attachmentsReference}
                  createAttachment={this.createAttachment}
                  attachments={this.state.attachments}
                  showForm={true}
                  handleRemove={this.removeAttachment}
                />
                <Button
                  type="primary"
                  onClick={this.createEmailTemplate}
                  className="base-button"
                  style={{
                    position: "relative",
                    borderRadius: 0,
                    height: "40px",
                    width: "150px",
                  }}
                >
                  Save Template
                </Button>
              </Col>
            </Row>
          </Spin>
        </div>
      </Card>
    );
  }
}
